import { useRef, useEffect, useState } from 'react';
import { motion, useInView, useTransform, useScroll } from 'framer-motion';
import { TrendingUp, ArrowRight } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/ai-scale-hq/30min';

// Animated counter component
const AnimatedNumber = ({ value, duration = 1.5, isInView, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [value, duration, isInView]);

  return <>{prefix}{count.toLocaleString()}{suffix}</>;
};

const roiSteps = [
  { value: 100, label: 'Leads', prefix: '', suffix: '' },
  { value: 15000, label: 'Ad Spend', prefix: '$', suffix: '' },
  { value: 15, label: 'Jobs at 15%', prefix: '', suffix: '' },
  { value: 30, label: 'Jobs at 30%', prefix: '', suffix: '' },
];

const followUpStats = [
  { stat: '80%', description: 'of sales happen between 5th-12th contact' },
  { stat: '95%', description: 'of leads reached by the 6th attempt' },
  { stat: '10%', description: 'of salespeople make 3+ attempts' },
  { stat: '63%', description: 'won\'t buy for at least 3 months' },
];

export const ROISection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.12, 0]);

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
      data-testid="roi-section"
    >
      {/* Animated background */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: backgroundY }}
      >
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#32D583]/10 blur-[150px]"
          style={{ opacity: glowOpacity }}
        />
      </motion.div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, filter: 'blur(20px)' }}
          animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center mb-12"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#32D583]/10 border border-[#32D583]/20 mb-6"
          >
            <TrendingUp className="w-4 h-4 text-[#32D583]" />
            <span className="text-sm text-[#32D583]">ROI Impact</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="roi-headline"
          >
            More Revenue From the Leads
            <br />
            <span className="text-[#32D583]">You Already Have</span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-white/60 max-w-xl mx-auto"
          >
            The pitch isn't "buy more leads." It's "stop wasting the leads you already paid for."
          </motion.p>
        </motion.div>

        {/* ROI comparison box */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="glass-card p-8 md:p-10">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-center mb-8"
            >
              <p className="text-white/60 mb-2">Same Ad Spend. Double the Jobs.</p>
              <h3 className="text-xl font-bold text-white">
                Average lead conversion: <span className="text-[#F04438]">11.8%</span>
              </h3>
            </motion.div>

            {/* Animated stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {roiSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="bg-white/5 rounded-xl p-4 text-center border border-white/5"
                >
                  <motion.div 
                    className="text-2xl md:text-3xl font-bold text-white mb-1"
                    animate={isInView ? { 
                      textShadow: ['0 0 0 rgba(108,77,255,0)', '0 0 20px rgba(108,77,255,0.3)', '0 0 0 rgba(108,77,255,0)']
                    } : {}}
                    transition={{ duration: 2, delay: 1 + index * 0.2, repeat: Infinity, repeatDelay: 3 }}
                  >
                    <AnimatedNumber 
                      value={step.value} 
                      isInView={isInView} 
                      prefix={step.prefix}
                      suffix={step.suffix}
                      duration={1.5 + index * 0.2}
                    />
                  </motion.div>
                  <div className="text-sm text-white/50">{step.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Animated comparison strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="p-5 rounded-xl bg-[#32D583]/10 border border-[#32D583]/20"
            >
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 1 }}
                  className="text-center"
                >
                  <div className="text-2xl font-bold text-[#F04438]">15%</div>
                  <div className="text-xs text-white/50">Old Close Rate</div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: 1.2 }}
                >
                  <ArrowRight className="w-5 h-5 text-[#32D583]" />
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 1.4 }}
                  className="text-center"
                >
                  <div className="text-2xl font-bold text-[#32D583]">30%</div>
                  <div className="text-xs text-white/50">New Close Rate</div>
                </motion.div>
                
                <div className="w-px h-10 bg-white/10 mx-3 hidden md:block" />
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 1.6, type: 'spring' }}
                  className="text-center"
                >
                  <motion.div 
                    className="text-2xl font-bold text-[#32D583]"
                    animate={isInView ? {
                      scale: [1, 1.1, 1],
                    } : {}}
                    transition={{ duration: 0.5, delay: 1.8 }}
                  >
                    2x
                  </motion.div>
                  <div className="text-xs text-white/50">More Revenue, Same Spend</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Follow-up stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="max-w-4xl mx-auto mb-10"
        >
          <h3 className="text-lg font-semibold text-white text-center mb-6">
            Most Contractors Stop Too Early
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {followUpStats.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
                whileHover={{ scale: 1.02, borderColor: 'rgba(108,77,255,0.3)' }}
                className="glass-card p-4 text-center transition-all"
              >
                <motion.div 
                  className="text-xl font-bold text-[#6C4DFF] mb-1"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ duration: 0.3, delay: 1.6 + index * 0.1 }}
                >
                  {item.stat}
                </motion.div>
                <p className="text-xs text-white/50">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA with glow animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.8 }}
          className="text-center"
        >
          <motion.a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            animate={isInView ? {
              boxShadow: [
                '0 0 20px rgba(108,77,255,0.3)',
                '0 0 40px rgba(108,77,255,0.5)',
                '0 0 20px rgba(108,77,255,0.3)',
              ]
            } : {}}
            transition={{ 
              boxShadow: { duration: 2, repeat: Infinity, delay: 2 }
            }}
            data-testid="roi-cta"
          >
            See Your Revenue Potential
            <ArrowRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
